// Upgrade Medic Types

export type RuntimeLanguage = 'python' | 'java' | 'nodejs' | 'dotnet' | 'go' | 'ruby' | 'other';

// Update Types - distinguishes between Lambda runtime and Brazil interpreter workflows
export type UpdateType = 'lambda_runtime' | 'interpreter_version';

// Target Versions for Lambda runtime updates (e.g., python3.12, nodejs20.x)
export interface TargetVersions {
  python?: string; // e.g., "python3.12", "python3.13"
  nodejs?: string; // e.g., "nodejs20.x", "nodejs22.x"
  java?: string; // e.g., "java17", "java21"
}

// Target Interpreter Versions for Brazil toolchain upgrades
export interface TargetInterpreterVersions {
  CPython3?: string; // e.g., "3.11", "3.12"
  NodeJS?: string; // e.g., "20", "22"
}

// Start Update Data - sent to backend via WebSocket
export interface StartUpdateData {
  pipelines: string[];
  updateType: UpdateType;
  targetVersions: TargetVersions;
  targetInterpreterVersions?: TargetInterpreterVersions; // Required for interpreter_version workflow
  userId: string;
  deployToAlpha?: boolean;
  functions?: string[];
}

// Manual Resources Update Data - for standalone lambdas and CFN stacks
export interface UpdateManualResourcesData {
  lambdas?: ManualLambda[];
  stacks?: ManualStack[];
  targetVersions: TargetVersions;
  userId: string;
}

// Manual Lambda for direct update
export interface ManualLambda {
  arn: string;
  functionName?: string;
  name?: string;
  region?: string;
  runtime?: string;
}

// Manual Stack for CFN update
export interface ManualStack {
  stackName: string;
  region: string;
  accountId?: string;
  lambdas?: ManualLambda[];
}

// Job Stage Enum - represents workflow stages
export enum JobStage {
  INITIALIZING = 'INITIALIZING', // Discovery phase - finding resources
  IMPACT_ANALYSIS = 'IMPACT_ANALYSIS', // Analyzing what needs to change
  CODE_CHANGES = 'CODE_CHANGES', // Implementing code changes
  BUILD = 'BUILD', // Building the package
  CR_CREATION = 'CR_CREATION', // Creating code review / release
  CLEANUP = 'CLEANUP', // Cleanup resources and finalize
  PIPELINE_TRIGGER = 'PIPELINE_TRIGGER', // Triggering deployment pipeline
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED'
}

// Resource Status - status of individual resources within a job
export type ResourceStatus = 'pending' | 'running' | 'completed' | 'failed' | 'skipped';

// Stage Info - tracks status and timing for each stage
export interface StageInfo {
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  startedAt?: string;
  completedAt?: string;
  duration?: number;
  message?: string;
  error?: string;
}

// Artifacts - links generated during job execution
export interface JobArtifacts {
  buildLink?: string;
  crLink?: string; // Single CR URL (deprecated, for backward compat)
  crLinks?: string[]; // Multiple CR URLs (new format)
  alphaDeployUrl?: string; // AgentSpaces alpha-deploy launch URL, built by CCE (single source of truth)
  pipelineLink?: string;
  manualActions?: string[]; // List of manual actions required by user
  stackNames?: string[]; // Stack names for Manual Deployed Stacks jobs
  stackToLambdas?: Record<string, string[]>; // Map of stackName -> lambdaNames[] for Manual Deployed Stacks display
}

// Error details for job failures
export interface JobError {
  code: string;
  message: string;
  stage: JobStage;
  stackTrace?: string;
  retryable?: boolean;
  failedAt?: string;
}

// Resource tracked within a job
export interface JobResource {
  resourceArn: string;
  resourceName: string;
  resourceType: 'lambda' | 'rds' | 'emr';
  accountName?: string;
  pipelineId?: string;
  fromVersion: string;
  targetVersion: string;
  status: ResourceStatus;
  startedAt?: string;
  completedAt?: string;
  progress?: number;
  progressMessage?: string;
  result?: {
    success: boolean;
    details?: string;
    error?: string;
  };
}

/**
 * Logical shape shared by every Upgrade Medic resource kind (Lambda functions,
 * EMR clusters, EMR Serverless applications). `type` is the display discriminator
 * ("Lambda" | "Cluster" | "Serverless"); `runtime` holds the version string
 * (Lambda runtime or EMR release label). Resource-kind-specific fields live on the
 * extending interfaces below.
 */
export interface UpgradeMedicResource {
  id: string;
  name: string;
  arn: string;
  type: string;
  runtime: string;
  status: 'Done' | 'Pending' | 'Failed' | 'Running';
  region: string;
  stackName?: string; // CloudFormation stack name for manual deployments
  // Authoritative EMR discriminator, absent on Lambda rows. Consumers that need to tell a
  // classic cluster from a serverless application MUST read this, never parse `type`.
  emrType?: 'cluster' | 'serverless';
}

/** A Lambda function resource. Alias retained so existing references keep working. */
export type LambdaFunction = UpgradeMedicResource;

/** An EMR resource — classic cluster or serverless application. */
export interface EmrResource extends UpgradeMedicResource {
  emrType: 'cluster' | 'serverless';
}

export interface Pipeline {
  id: string;
  name: string;
  lambdas: LambdaFunction[];
  isDeleted?: boolean;
  isManual?: boolean;
}

export interface Account {
  id: string;
  name: string;
  pipelines: Pipeline[];
}

export interface RuntimeVersion {
  value: string;
  label: string;
  isRecommended?: boolean;
  isLatest?: boolean;
}

export interface RuntimeConfig {
  language: RuntimeLanguage;
  displayName: string;
  icon: string;
  versions: RuntimeVersion[];
  currentRuntimes: string[]; // Runtime strings that map to this language
}

/** Shared job status type used across all upgrade medic components. */
export type JobStatus = 'idle' | 'connecting' | 'running' | 'completed' | 'completed_with_alpha_deployment_failure' | 'failed';

/** Status type for persisted job runs (no 'idle', adds 'cancelled'). */
export type JobRunStatus = Exclude<JobStatus, 'idle'> | 'cancelled';

export interface UpdateJob {
  id: string;
  status: JobStatus;
  progress: number;
  message: string;
  startedAt?: Date;
  completedAt?: Date;
}

export interface AgentLogEntry {
  id: number;
  timestamp: Date;
  level: 'info' | 'warning' | 'error' | 'agent' | 'phase' | 'tool' | 'ephemeral';
  stage?: string;
  message: string;
  fullText?: string; // Full text for ephemeral entries (used for expanded view)
  isCollapsed?: boolean; // Whether ephemeral entry is collapsed (default: true)
}

export interface SelectedVersions {
  [language: string]: string;
}

export interface DeploymentOptions {
  deployToAlpha: boolean;
}

// Pipeline execution status for parallel processing
export interface PipelineExecution {
  pipelineId: string;
  subJobId: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  startedAt?: Date;
  completedAt?: Date;
  progress: number;
  progressMessage?: string;
  currentStage?: JobStage;
  stages: Record<string, StageInfo>;
  error?: string;
  artifacts: JobArtifacts;
  streamingText?: string;
}

// Job Run tracking - enhanced with stages, artifacts, and error tracking
export interface JobRun {
  jobId: string;
  pipelineId?: string;
  status: JobRunStatus;
  triggeredBy: string;
  startedAt: Date;
  completedAt?: Date;
  lastUpdated?: Date;

  // Target info
  pipelines: string[];
  pipelineExecutions?: Record<string, PipelineExecution>; // pipelineId -> execution state for parallel processing
  functions: string[];
  targetVersions: Record<string, string>;
  resourceType: string;

  // Progress tracking
  progress: number;
  progressMessage: string;
  currentStage?: JobStage;

  // Stage tracking - status of each workflow stage
  stages: Record<string, StageInfo>;

  // Artifacts - links generated during execution
  artifacts: JobArtifacts;

  // Error details on failure
  error?: JobError;

  // Resources being processed
  resources?: JobResource[];
  totalResources?: number;
  completedResources?: number;
  failedResources?: number;

  // Logs and streaming
  logs: AgentLogEntry[];
  streamingText: string;

  // Final result summary
  result?: {
    success: number;
    failed: number;
    total: number;
  };
}

// Initialize default stages for a new job.
// 5 stages without deploy, 6 stages when deployToAlpha is true (adds PIPELINE_TRIGGER before CLEANUP).
export const createInitialStages = (deployToAlpha = false): Record<string, StageInfo> => ({
  [JobStage.INITIALIZING]: { status: 'pending' },
  [JobStage.IMPACT_ANALYSIS]: { status: 'pending' },
  [JobStage.CODE_CHANGES]: { status: 'pending' },
  [JobStage.BUILD]: { status: 'pending' },
  ...(deployToAlpha ? { [JobStage.PIPELINE_TRIGGER]: { status: 'pending' } } : {}),
  [JobStage.CLEANUP]: { status: 'pending' }
});

// ==========================================
// BRAZIL EOL INTERPRETER TYPES
// ==========================================

// VersionSet detail from DynamoDB
export interface VersionSetDetail {
  pipeline: string;
  versionSet: string;
  cpythonRuntime: string;
  nodejsRuntime: string;
}

// Group of versionsets under a pipeline
export interface VersionSetPipelineGroup {
  name: string;
  versionsets: VersionSetDetail[];
}

// Group of pipelines under a runtime version
export interface RuntimeVersionGroup {
  runtime: string;
  isEOL: boolean;
  pipelines: VersionSetPipelineGroup[];
  totalCount: number;
}

// Complete data structure for Brazil EOL Interpreter card
export interface BrazilEOLInterpreterData {
  totalVersionsets: number;
  cpythonEOLCount: number;
  nodejsEOLCount: number;
  cpythonTotalCount: number; // Total CPython versionsets (all versions)
  nodejsTotalCount: number; // Total NodeJS versionsets (all versions)
  cpythonGroups: RuntimeVersionGroup[];
  nodejsGroups: RuntimeVersionGroup[];
  eolVersions: {
    cpython: string[];
    nodejs: string[];
  };
  currentVersions: {
    cpython: string[];
    nodejs: string[];
  };
}

// ==========================================
// AWS ACCOUNT DETAILS TYPES
// ==========================================

/** Account metadata from the aws-account-details-cdk DynamoDB table */
export interface AccountDetailsRecord {
  accountId: string;
  accountName: string;
  manager?: string;
  owner?: string;
  team?: string;
  leadershipHierarchy?: string;
}

/** Lookup map: accountName → { manager, owner, team } */
export type AccountDetailsMap = Map<string, AccountDetailsRecord>;
